import React from 'react';
import { Descriptions, Divider, Avatar, Space, Button, Popconfirm, Tag, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import {
    DeleteOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CrownOutlined,
    SafetyOutlined,
    DollarOutlined
} from '@ant-design/icons';

const { Text } = Typography;

const UserInfo = ({ user, onDeleteProfilePicture }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();

    if (!user) {
        return null;
    }

    const formatNumber = (num) => {
        return new Intl.NumberFormat().format(num || 0);
    };

    const renderBooleanTag = (value, label) => {
        return value ? (
            <Tag icon={<CheckCircleOutlined />} color="success">{label || t('yes')}</Tag>
        ) : (
            <Tag icon={<CloseCircleOutlined />} color="default">{label || t('no')}</Tag>
        );
    };

    return (
        <>
            {/* Profile Header */}
            <Space
                direction="horizontal"
                size={16}
                align="start"
                style={{ marginBottom: 24, width: '100%' }}
            >
                <Avatar
                    src={user.picture_url}
                    size={80}
                    style={{ border: `2px solid ${token.colorBorder}`, flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong style={{ fontSize: 18, display: 'block' }}>
                        {user.full_name}
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                        {user.email}
                    </Text>
                    {user.picture_url && onDeleteProfilePicture && (
                        <Popconfirm
                            title={t('delete_profile_picture')}
                            okText={t('yes')}
                            cancelText={t('no')}
                            onConfirm={() => onDeleteProfilePicture(user.id)}
                        >
                            <Button
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                style={{ marginTop: 8 }}
                            >
                                {t('delete_profile_picture')}
                            </Button>
                        </Popconfirm>
                    )}
                </div>
            </Space>

            {/* Basic Information */}
            <Descriptions
                bordered
                column={{ xs: 1, sm: 2 }}
                size="small"
                labelStyle={{ fontWeight: 600, width: '40%' }}
                contentStyle={{ width: '60%' }}
            >
                <Descriptions.Item label={t('gender')}>
                    {user.gender || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('locale')}>
                    {user.locale || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('timezone')}>
                    {user.timezone || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('account_active')}>
                    {renderBooleanTag(!user.is_disabled)}
                </Descriptions.Item>
                <Descriptions.Item label={t('admin_access')}>
                    {user.is_admin ? (
                        <Tag icon={<CrownOutlined />} color="gold">{t('yes')}</Tag>
                    ) : (
                        <Tag color="default">{t('no')}</Tag>
                    )}
                </Descriptions.Item>
                <Descriptions.Item label={t('verified')}>
                    {user.is_verified ? (
                        <Tag icon={<SafetyOutlined />} color="blue">{t('yes')}</Tag>
                    ) : (
                        <Tag color="default">{t('no')}</Tag>
                    )}
                </Descriptions.Item>
            </Descriptions>

            {/* Account Details */}
            <Divider orientation="left">{t('account_details')}</Divider>
            <Descriptions
                bordered
                column={{ xs: 1, sm: 2 }}
                size="small"
                labelStyle={{ fontWeight: 600, width: '40%' }}
                contentStyle={{ width: '60%' }}
            >
                <Descriptions.Item label={t('created_at')}>
                    {moment(user.created_at).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
                <Descriptions.Item label={t('updated_at')}>
                    {moment(user.updated_at).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
                <Descriptions.Item label={t('welcome_credit_at')}>
                    {user.welcome_credit_at
                        ? moment(user.welcome_credit_at).format('YYYY-MM-DD HH:mm:ss')
                        : <Text type="secondary">{t('not_available')}</Text>
                    }
                </Descriptions.Item>
                <Descriptions.Item label={t('discount_added_at')}>
                    {user.discount_added_at
                        ? moment(user.discount_added_at).format('YYYY-MM-DD HH:mm:ss')
                        : <Text type="secondary">{t('not_available')}</Text>
                    }
                </Descriptions.Item>
                <Descriptions.Item label={t('discount_percentage')}>
                    {user.discount_percentage ? (
                        <Tag color="orange">{user.discount_percentage}%</Tag>
                    ) : (
                        <Text type="secondary">0%</Text>
                    )}
                </Descriptions.Item>
                <Descriptions.Item label={t('discount_expiry')}>
                    {user.discount_expiry
                        ? moment(user.discount_expiry).format('YYYY-MM-DD')
                        : <Text type="secondary">{t('not_available')}</Text>
                    }
                </Descriptions.Item>
                <Descriptions.Item label={t('discount_added_by')} span={2}>
                    {user.discount_added_by?.full_name || <Text type="secondary">{t('not_available')}</Text>}
                </Descriptions.Item>
                <Descriptions.Item label={t('note')} span={2}>
                    {user.note || <Text type="secondary">{t('not_available')}</Text>}
                </Descriptions.Item>
            </Descriptions>

            {/* Balance Details */}
            <Divider orientation="left">{t('balance_details')}</Divider>
            <Descriptions
                bordered
                column={{ xs: 1, sm: 2 }}
                size="small"
                labelStyle={{ fontWeight: 600, width: '40%' }}
                contentStyle={{ width: '60%' }}
            >
                <Descriptions.Item label={t('available_coins')}>
                    <Text strong style={{ color: token.colorSuccess }}>
                        <DollarOutlined /> {formatNumber(user.balance?.available_coins)}
                    </Text>
                </Descriptions.Item>
                <Descriptions.Item label={t('total_coins_bought')}>
                    <Text>{formatNumber(user.balance?.total_coins_bought)}</Text>
                </Descriptions.Item>
                <Descriptions.Item label={t('total_coins_consumed')}>
                    <Text>{formatNumber(user.balance?.total_coins_consumed)}</Text>
                </Descriptions.Item>
            </Descriptions>
        </>
    );
};

export default UserInfo;
